/**
 * CR-5865: one row shape for everything a supplier has been asked to complete.
 *
 * Helix keeps questionnaire assignments in two places — onboarding-side
 * `FilledDataset` rows and Supplier Intelligence `SupplierQualification` rows —
 * with two status vocabularies that mean the same things. The supplier does
 * not care which system a customer used, so this module folds both into one
 * row, one status vocabulary, one "what do I need to do" verb, and one rule
 * for when a score may be shown.
 */
import type { FilledDataset, SupplierQualification } from '../../lib/api';

export type QuestionnaireKind = 'dataset' | 'qualification';

/** The supplier-facing status vocabulary. Stored statuses map onto it. */
export type DisplayStatus =
  | 'not_started'
  | 'in_progress'
  | 'submitted'
  | 'completed'
  | 'more_info_required'
  | 'rejected'
  | 'expired';

export type RequiredAction = 'start' | 'continue' | 'provide_info' | 'resubmit' | 'renew' | 'none';

export interface SupplierQuestionnaireRow {
  /** Stable across both kinds: `dataset-12` / `qualification-7`. */
  key: string;
  kind: QuestionnaireKind;
  id: number;
  name: string;
  customerName: string;
  typeLabel: string;
  recurring: boolean;
  /** The stored status, kept for anything that needs the source vocabulary. */
  rawStatus: string;
  status: DisplayStatus;
  /** Past its expiry while still waiting on the supplier or the customer. */
  isOverdue: boolean;
  dueDate: string | null;
  assignedAt: string | null;
  startedAt: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  updatedAt: string | null;
  completion: number | null;
  scorePercentage: number | null;
  passed: boolean | null;
  /** Whether the score / result may be shown to the supplier at all. */
  showScore: boolean;
  action: RequiredAction;
  /** Customer feedback the supplier is entitled to read (rejection / more-info). */
  feedback: string | null;
  daysUntilExpiry: number | null;
}

export const STATUS_LABELS: Record<DisplayStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  submitted: 'Submitted',
  completed: 'Completed',
  more_info_required: 'More Info Required',
  rejected: 'Rejected',
  expired: 'Expired',
};

export const STATUS_ORDER: DisplayStatus[] = [
  'not_started', 'in_progress', 'more_info_required', 'rejected', 'submitted', 'completed', 'expired',
];

export const ACTION_LABELS: Record<RequiredAction, string> = {
  start: 'Start',
  continue: 'Continue',
  provide_info: 'Provide more info',
  resubmit: 'Resubmit',
  renew: 'Renew',
  none: 'None',
};

const DATASET_TYPE_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  compliance: 'Compliance',
  risk_assessment: 'Risk Assessment',
  periodic_review: 'Periodic Review',
  code_of_conduct: 'Code of Conduct',
  capability: 'Capability Assessment',
  esg: 'ESG / Sustainability',
  financial: 'Financial Assessment',
  quality: 'Quality',
  custom: 'Ad hoc',
};

const TEMPLATE_TYPE_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  standard: 'Standard',
  compliance: 'Compliance',
  audit: 'Audit',
  esg: 'ESG / Sustainability',
  quality: 'Quality',
  custom: 'Ad hoc',
};

/** Both stored vocabularies onto the supplier-facing one. */
export function toDisplayStatus(raw: string): DisplayStatus {
  switch (raw) {
    case 'not_started':
    case 'invited':
      return 'not_started';
    case 'in_progress':
      return 'in_progress';
    case 'submitted':
    case 'under_review':
      return 'submitted';
    case 'approved':
      return 'completed';
    case 'more_info_required':
      return 'more_info_required';
    case 'rejected':
      return 'rejected';
    case 'expired':
      return 'expired';
    default:
      return 'not_started';
  }
}

const AWAITING = new Set(['not_started', 'invited', 'in_progress', 'more_info_required', 'submitted', 'under_review']);

/** Overdue is derived, never stored: the expiry has passed and the row is still open. */
export function isOverdue(rawStatus: string, expiresAt: string | null | undefined, now: Date): boolean {
  if (!expiresAt || !AWAITING.has(rawStatus)) return false;
  const expiry = new Date(expiresAt);
  return !Number.isNaN(expiry.getTime()) && expiry < now;
}

function daysUntil(expiresAt: string | null | undefined, now: Date): number | null {
  if (!expiresAt) return null;
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return null;
  return Math.floor((expiry.getTime() - now.getTime()) / 86_400_000);
}

export const RENEW_WINDOW_DAYS = 30;

export function requiredAction(rawStatus: string, daysUntilExpiry: number | null): RequiredAction {
  switch (rawStatus) {
    case 'not_started':
    case 'invited':
      return 'start';
    case 'in_progress':
      return 'continue';
    case 'more_info_required':
      return 'provide_info';
    case 'rejected':
      return 'resubmit';
    case 'approved':
      return daysUntilExpiry != null && daysUntilExpiry <= RENEW_WINDOW_DAYS ? 'renew' : 'none';
    case 'expired':
      return 'renew';
    default:
      return 'none';
  }
}

/**
 * Suppliers never see scoring while a questionnaire is open (sealed-bid rule),
 * so a score is shown only once the customer has decided. Onboarding datasets
 * additionally carry the customer's own "share the score" switch.
 */
export function canShowScore(kind: QuestionnaireKind, rawStatus: string, shareFlag: boolean | undefined): boolean {
  const reviewed = rawStatus === 'approved' || rawStatus === 'rejected';
  if (!reviewed) return false;
  return kind === 'dataset' ? shareFlag === true : true;
}

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

export function fromFilledDataset(d: FilledDataset, now: Date): SupplierQuestionnaireRow {
  const days = d.days_until_expiry ?? daysUntil(d.expires_at, now);
  const typeLabel = DATASET_TYPE_LABELS[d.dataset_type ?? ''] ?? (d.onboarding ? 'Onboarding' : 'Questionnaire');
  return {
    key: `dataset-${d.id}`,
    kind: 'dataset',
    id: d.id,
    name: d.dataset_name ?? 'Questionnaire',
    customerName: d.customer_name ?? '',
    typeLabel,
    recurring: false,
    rawStatus: d.status,
    status: toDisplayStatus(d.status),
    isOverdue: isOverdue(d.status, d.expires_at, now),
    dueDate: d.expires_at ?? null,
    assignedAt: d.assigned_at ?? null,
    startedAt: d.started_at ?? null,
    submittedAt: d.submitted_at ?? null,
    reviewedAt: d.reviewed_at ?? null,
    updatedAt: d.updated_at ?? d.reviewed_at ?? d.submitted_at ?? d.started_at ?? d.assigned_at ?? null,
    completion: toNumber(d.completion_percentage),
    scorePercentage: toNumber(d.score_percentage),
    passed: d.passed ?? null,
    showScore: canShowScore('dataset', d.status, d.show_score_to_supplier),
    action: requiredAction(d.status, days),
    feedback: d.status === 'rejected' ? (d.rejection_reason || null)
      : d.status === 'more_info_required' ? (d.more_info_feedback || null) : null,
    daysUntilExpiry: days,
  };
}

export function fromQualification(q: SupplierQualification, now: Date): SupplierQuestionnaireRow {
  const days = daysUntil(q.expires_at, now);
  return {
    key: `qualification-${q.id}`,
    kind: 'qualification',
    id: q.id,
    name: q.template_name ?? 'Questionnaire',
    customerName: q.customer_name ?? '',
    typeLabel: TEMPLATE_TYPE_LABELS[q.template_type ?? ''] ?? 'Questionnaire',
    recurring: q.is_recurring === true,
    rawStatus: q.status,
    status: toDisplayStatus(q.status),
    isOverdue: isOverdue(q.status, q.expires_at, now),
    dueDate: q.expires_at ?? null,
    assignedAt: q.invited_at ?? null,
    startedAt: q.started_at ?? null,
    submittedAt: q.submitted_at ?? null,
    reviewedAt: q.reviewed_at ?? q.approved_at ?? null,
    updatedAt: q.updated_at ?? null,
    completion: toNumber(q.completion_percentage),
    scorePercentage: toNumber(q.score_percentage),
    passed: q.passed ?? null,
    showScore: canShowScore('qualification', q.status, undefined),
    action: requiredAction(q.status, days),
    feedback: q.status === 'rejected' ? (q.rejection_reason || q.feedback_to_supplier || null)
      : q.status === 'more_info_required' ? (q.feedback_to_supplier || null) : null,
    daysUntilExpiry: days,
  };
}

const time = (iso: string | null): number => (iso ? new Date(iso).getTime() || 0 : 0);

/** Both sources into one list, most recently touched first. */
export function mergeRows(
  datasets: FilledDataset[],
  qualifications: SupplierQualification[],
  now: Date = new Date(),
): SupplierQuestionnaireRow[] {
  return [
    ...datasets.map((d) => fromFilledDataset(d, now)),
    ...qualifications.map((q) => fromQualification(q, now)),
  ].sort((a, b) => time(b.updatedAt) - time(a.updatedAt));
}

export function filterRows(
  rows: SupplierQuestionnaireRow[],
  search: string,
  status: DisplayStatus | 'all' | 'overdue',
): SupplierQuestionnaireRow[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (status === 'overdue' ? !row.isOverdue : status !== 'all' && row.status !== status) return false;
    if (!needle) return true;
    return (
      row.name.toLowerCase().includes(needle)
      || row.customerName.toLowerCase().includes(needle)
      || row.typeLabel.toLowerCase().includes(needle)
    );
  });
}

/** Where each action takes the supplier. Both runners already exist. */
export function rowHref(row: SupplierQuestionnaireRow): string {
  return row.kind === 'dataset'
    ? `/onboarding/fill/${row.id}`
    : `/intelligence/qualifications/${row.id}?returnTo=${encodeURIComponent('/questionnaires')}`;
}
